import { useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  FUNDRAISERS_SLUG,
  isFundraisersPath,
  type FundraiserModuleNavigation,
} from '@givmo/fundraisers-module';
import { useCharityConsole } from '../../console/charity-console-context';
import { useRequestAccessDoor } from '../../components/request-access-context';
import { useModuleNavigate } from '../../console/use-console-squares';
import { useTurnOnHref } from '../../console/use-turn-on-href';
import { isFundraisersModuleEnabled } from '../../lib/env';

/** The existing host read and request door, shared by the module page and financial links. */
export function useFundraisersNavigation(): FundraiserModuleNavigation & {
  onNavigate: (href: string) => void;
} {
  const { pending, errored, standings, refetch } = useCharityConsole();
  const door = useRequestAccessDoor();
  const switchModule = useModuleNavigate();
  const routerNavigate = useNavigate();
  // A hop INSIDE the module carries no frame, so the page keeps the frame it wears — on a page
  // borrowed into Donations, the Donations frame (the last-module hint holds it; org-console-shell.md
  // § Sidebar). A door OUT of the module (the gate's "Turn on" into /org) is a module switch.
  const navigate = useCallback(
    (href: string) => {
      if (isFundraisersPath(href)) void routerNavigate({ href });
      else switchModule(href);
    },
    [routerNavigate, switchModule],
  );
  const turnOnHref = useTurnOnHref(FUNDRAISERS_SLUG);
  const standing = standings?.[FUNDRAISERS_SLUG];
  const request = standing?.accessRequest;
  return {
    read: { pending, errored, standings },
    mounted: isFundraisersModuleEnabled(),
    onRetry: refetch,
    turnOnHref,
    onNavigate: navigate,
    onRequestAccess: standing?.held === true ? undefined : () => door.open(FUNDRAISERS_SLUG),
    accessRequest:
      request?.status === 'PENDING'
        ? { status: 'pending' }
        : request?.status === 'DENIED'
          ? { status: 'declined', note: request.resolutionNote ?? null }
          : undefined,
  };
}
